/**
 * Reading the request log — UX-01.
 *
 * WHY THE DECISIONS LIVE HERE AND NOT IN THE PAGE
 *
 * The page is a table. What is worth getting right is not the table: it is
 * what the empty state says, what a sampled row means, and whether a number
 * on screen is a count of requests or a count of rows. Those are sentences
 * and predicates, so they sit in lib where a test can hold them - the same
 * arrangement domain-state and policy-form use.
 *
 * THE THING THIS PAGE MUST NOT DO
 *
 * Analytics Engine SAMPLES. A row can stand for ten requests, and it says
 * so in `sampleInterval`. A log page that counts rows and calls the number
 * "requests" is wrong by a factor nobody can see, and wrong in the
 * direction that makes a problem look smaller than it is. So the total is
 * computed from the intervals, and a sampled row is marked as one.
 *
 * "NO LOGS" AND "NO LOGGING" ARE DIFFERENT ANSWERS
 *
 * One means the app is quiet. The other means nobody will ever see a line
 * here until an operator fixes something. Showing the same empty state for
 * both is how a broken pipeline goes unnoticed for a week.
 */

import type { LogEntry } from './api.js'

export type LogWindow = '15m' | '1h' | '24h' | '7d'

export const WINDOWS: { value: LogWindow; label: string }[] = [
  { value: '15m', label: 'Last 15 minutes' },
  { value: '1h', label: 'Last hour' },
  { value: '24h', label: 'Last 24 hours' },
  { value: '7d', label: 'Last 7 days' },
]

export const LEVELS = ['error', 'warn', 'info'] as const
export type LogLevel = (typeof LEVELS)[number]

/**
 * The routes a filter may name — UX-01.
 *
 * Imported from the package the Worker writes with and the control plane
 * reads with, never re-typed here. The last hand-kept copy of this list
 * silently DROPPED any route missing from it out of the query's WHERE
 * clause, so asking for one kind of traffic returned all of it; the fix
 * was one definition rather than a test comparing two.
 *
 * The API refuses a route it does not know, so a copy that drifted would
 * now produce a 400 from a control the dashboard drew itself.
 */
export { ROUTE_PATTERNS as ROUTES } from '@nimblybase/request-log'

/**
 * How many rows the page asks for.
 *
 * Exported because the honesty below depends on knowing it: a view capped
 * at this number and a view that happens to contain this many are
 * indistinguishable from the answer alone.
 */
export const PAGE_LIMIT = 200

/**
 * Whether this view might be hiding something, said without overclaiming.
 *
 * At exactly the limit there is no way to tell a full page from a capped
 * one - the API returns rows, not a total - so this says "may be" and
 * points at the two filters that make the question answerable. Saying
 * "there are more" would be a guess, and saying nothing leaves somebody
 * reading a truncated log as if it were the whole one.
 */
export function truncationNote(entries: readonly LogEntry[], limit = PAGE_LIMIT): string | null {
  if (entries.length < limit) return null
  return `Showing the most recent ${limit}. There may be more — narrow the time window or pick a route.`
}

/**
 * How many requests these rows stand for.
 *
 * Not `entries.length`. A sampled row represents `sampleInterval` requests,
 * and reporting the row count as a request count under-reports exactly the
 * traffic that triggered sampling - the busy periods somebody is looking at
 * the log to understand.
 */
export function requestsRepresented(entries: readonly LogEntry[]): number {
  return entries.reduce((total, e) => total + Math.max(1, Math.trunc(e.sampleInterval || 1)), 0)
}

/** Whether any row here stands for more than itself. */
export const isSampled = (entries: readonly LogEntry[]): boolean =>
  entries.some((e) => e.sampleInterval > 1)

/**
 * What to say when the table is empty.
 *
 * Three different situations, three different next actions - and the one
 * thing that must never happen is presenting a broken log pipeline as a
 * quiet application.
 */
export function emptyState(input: {
  unavailable?: { reason: string }
  window: LogWindow
  filtered: boolean
}): { title: string; detail: string; severity: 'info' | 'problem' } {
  if (input.unavailable) {
    return {
      severity: 'problem',
      title: 'Logs are unavailable',
      // The reason comes from the API, which knows what actually failed.
      detail: `${input.unavailable.reason} This is not the same as having no traffic - `
        + 'requests may be arriving and going unrecorded.',
    }
  }
  if (input.filtered) {
    return {
      severity: 'info',
      title: 'Nothing matches these filters',
      detail: 'Clear a filter, or widen the time window.',
    }
  }
  return {
    severity: 'info',
    title: `No requests in the ${label(input.window).toLowerCase()}`,
    detail: 'New lines can take a few minutes to appear. Refresh after a short wait. If it stays '
      + "empty, check that your app is calling this project's API endpoint.",
  }
}

export const label = (window: LogWindow): string =>
  WINDOWS.find((w) => w.value === window)?.label ?? 'selected window'

/** The pill class for a level. Failures must be visible without reading. */
export const levelClass = (level: LogEntry['level']): string =>
  level === 'error' ? 'pill pill-dead' : level === 'warn' ? 'pill pill-warn' : 'pill'

/**
 * Whether this row is worth expanding.
 *
 * A row with a message or a statement has something underneath it; one
 * without does not, and an expander that opens onto nothing teaches people
 * to stop clicking expanders.
 */
export const hasDetail = (entry: LogEntry): boolean =>
  Boolean(entry.message) || Boolean(entry.statement)

/** Milliseconds, at a precision that does not imply more than we measured. */
export function duration(ms: number): string {
  if (!Number.isFinite(ms) || ms < 0) return '-'
  if (ms < 1) return '<1 ms'
  if (ms < 1000) return `${Math.round(ms)} ms`
  return `${(ms / 1000).toFixed(ms < 10_000 ? 2 : 1)} s`
}

/** Local time, seconds included: the question is usually "was that mine?". */
export function at(timestamp: string): string {
  const date = new Date(timestamp)
  if (Number.isNaN(date.getTime())) return timestamp
  return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' })
}

/**
 * The summary line above the table.
 *
 * Says requests, not rows, and says when those are different. Silence about
 * sampling is what makes a count look exact when it is not.
 */
export function summarise(entries: readonly LogEntry[]): string {
  if (entries.length === 0) return ''
  const requests = requestsRepresented(entries)
  const failures = entries.filter((e) => e.level === 'error').length
  const parts = [`${entries.length} line${entries.length === 1 ? '' : 's'}`]
  if (requests !== entries.length) parts.push(`standing for about ${requests} requests (sampled)`)
  if (failures > 0) parts.push(`${failures} failed`)
  return parts.join(' · ')
}
